import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BookX, ShoppingBag, GraduationCap, Sun, Moon, Eye, BarChart2, Lock, X, ChevronLeft } from 'lucide-react';
import PreviewCardActionsMenu from '../components/PreviewCardActionsMenu';
import { SupportContent } from '../../components/study/SupportContent';
import { useTheme } from '../../contexts/ThemeContext';
import Toast from '../../components/Toast';
import '../../pages/StudyPage.css';
import '../../components/study/CardActionsMenu.css';
import '../../components/study/StudySidebarPanel.css';
import './RyanFlashcardPreviewPage.css';

interface CardData {
  _id: string;
  front: string;
  back: string;
  supportContent?: string;
  media?: {
    images?: Array<{ url: string; side: string }>;
  };
}

interface PreviewData {
  spaceName: string;
  subdeckName: string;
  cards: CardData[];
  maxCardsPerSession: number | null;
  productSlug: string | null;
}

type ErrorReason = 'not_found' | 'expired';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Fake progress data for the blurred sidebar
const FAKE_DIST_ROWS = [
  { label: 'Dominados', color: '#184C18', count: 22, pct: 28 },
  { label: 'Revisando', color: '#2F7130', count: 18, pct: 22 },
  { label: 'Aprendendo', color: '#ca8a04', count: 15, pct: 19 },
  { label: 'Não vistos', color: '#9CA3AF', count: 25, pct: 31 },
];

const RyanFlashcardPreviewPage: React.FC = () => {
  const { urlSlug } = useParams<{ urlSlug: string }>();
  const navigate = useNavigate();
  // Aberta pelo selo "Prévia" da RyanFieldPage (aluno logado, sem o produto):
  // mostra o "voltar" pro campo — a prévia acontece dentro da plataforma
  const location = useLocation();
  const returnPath = (location.state as { returnPath?: string } | null)?.returnPath || null;
  const { theme, toggleTheme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ reason: ErrorReason; productSlug?: string | null } | null>(null);
  const [data, setData] = useState<PreviewData | null>(null);

  const [cards, setCards] = useState<CardData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [cardsRated, setCardsRated] = useState(0);
  const [ratingHistory, setRatingHistory] = useState<number[]>([]);
  const [isExhausted, setIsExhausted] = useState(false);
  const [exhaustReason, setExhaustReason] = useState<'completed' | 'limit'>('completed');

  const [showSupportContent, setShowSupportContent] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [statsTab, setStatsTab] = useState<'session' | 'progress'>('session');

  useEffect(() => {
    if (!urlSlug) return;
    fetch(`/api/ryan/previa-cards/${urlSlug}`)
      .then(r => r.json())
      .then(d => {
        if (!d.valid) {
          setError({ reason: d.reason as ErrorReason, productSlug: d.productSlug });
        } else {
          setData(d);
          setCards(shuffle(d.cards as CardData[]));
        }
      })
      .catch(() => setError({ reason: 'not_found' }))
      .finally(() => setLoading(false));
  }, [urlSlug]);

  const handleFlip = () => {
    if (isTransitioning) return;
    setIsFlipped(f => !f);
  };

  const handleRate = useCallback(async (rating: number) => {
    if (isTransitioning) return;
    const newCount = cardsRated + 1;
    setCardsRated(newCount);
    if (rating > 0) setRatingHistory(h => [...h, rating]);
    setShowSupportContent(false);

    const limit = data?.maxCardsPerSession;
    if (limit && newCount >= limit) {
      setIsExhausted(true);
      setExhaustReason('limit');
      return;
    }
    if (currentIndex + 1 >= cards.length) {
      setIsExhausted(true);
      setExhaustReason('completed');
      return;
    }

    setIsTransitioning(true);
    setIsFlipped(false);
    await new Promise(r => setTimeout(r, 80));
    setCurrentIndex(i => i + 1);
    setIsTransitioning(false);
  }, [isTransitioning, cardsRated, data, currentIndex, cards.length]);

  const renderCardContent = (html: string | undefined) => {
    if (!html) return { __html: '' };
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('.delete-img-btn').forEach(b => b.remove());
    return { __html: div.innerHTML };
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="rfpp-root">
        <div className="rfpp-loading-screen">
          <div className="rfpp-spinner" />
          <p>Carregando prévia...</p>
        </div>
      </div>
    );
  }

  // ── Error / Expired ──
  if (error) {
    return (
      <div className="rfpp-root">
        <div className="rfpp-error-screen">
          <BookX size={56} strokeWidth={1.5} className="rfpp-error-icon" />
          <h2 className="rfpp-error-title">
            {error.reason === 'expired' ? 'Prévia expirada' : 'Link inválido'}
          </h2>
          <p className="rfpp-error-desc">
            {error.reason === 'expired'
              ? 'Este link de prévia não está mais disponível.'
              : 'Este link de prévia não existe ou foi revogado.'}
          </p>
          {error.productSlug && (
            <button
              className="rfpp-cta-btn"
              onClick={() => navigate(`/ryan/produto/${error.productSlug}`)}
            >
              <ShoppingBag size={16} />
              Ver produto completo
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data || cards.length === 0) {
    return (
      <div className="rfpp-root">
        <div className="rfpp-error-screen">
          <BookX size={56} strokeWidth={1.5} className="rfpp-error-icon" />
          <h2 className="rfpp-error-title">Nenhum card disponível</h2>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];
  const hasSupportContent = !!(currentCard.supportContent?.trim());
  const progress = isExhausted ? 100 : Math.round((currentIndex / cards.length) * 100);
  const last5 = ratingHistory.slice(-5);

  return (
    <div className="study-page-final">

      {/* ── Header: 3-column grid ── */}
      <div className="header-final">

        {/* Col 1: deck name + subdeck with badge inline */}
        <div className="rfpp-header-left-col">
          {returnPath && (
            <button className="rfpp-back-btn" onClick={() => navigate(returnPath)} title="Voltar ao campo" aria-label="Voltar ao campo">
              <ChevronLeft size={18} />
            </button>
          )}
          <div className="deck-info-final">
            <span className="deck-title-final">{data.spaceName}</span>
            <div className="rfpp-subdeck-row">
              <span className="subdeck-title-final">{data.subdeckName}</span>
              <span className="rfpp-preview-tag"><Eye size={10} />Prévia</span>
            </div>
          </div>
        </div>

        {/* Col 2: progress bar + counter */}
        {isExhausted ? (
          <div />
        ) : (
          <div className="rfpp-header-center">
            <div className="rfpp-progress-bar">
              <div className="rfpp-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="rfpp-counter">{currentIndex + 1} / {cards.length}</span>
          </div>
        )}

        {/* Col 3: domain bar + history + dark mode + stats */}
        <div className="rfpp-header-right-col">
          <div className="mastery-bar-container">
            <div className="mastery-label-col">
              <span className="mastery-label">Domínio</span>
              <span className="mastery-percentage rfpp-mastery-locked">—</span>
            </div>
            <div className="mastery-bar-wrapper-col">
              <div className="mastery-bar-row">
                <div className="mastery-bar-bg rfpp-bar-locked">
                  <div className="mastery-bar-fill" style={{ width: '0%' }} />
                </div>
              </div>
              <div className="card-history-row">
                <span className="card-history-label">Histórico</span>
                <div className="card-history-dots-animated" key={last5.join(',')}>
                  {last5.length === 0 ? (
                    <span className="card-history-empty">—</span>
                  ) : (
                    last5.map((rating, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="card-history-sep">›</span>}
                        <div className={`card-history-dot rating-dot-${rating}`}>{rating}</div>
                      </React.Fragment>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <button className="btn-toggle-theme" onClick={toggleTheme} aria-label="Alternar tema">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button className="btn-toggle-sidebar" onClick={() => setShowStats(v => !v)} aria-label="Estatísticas">
            <BarChart2 size={18} />
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      {isExhausted ? (
        <div className="rfpp-exhausted-screen">
          <div className="rfpp-exhausted-card">
            <GraduationCap size={52} color="#0D3B0E" strokeWidth={1.5} />
            <h2 className="rfpp-exhausted-title">
              {exhaustReason === 'completed' ? 'Prévia concluída!' : 'Limite de cards atingido'}
            </h2>
            <p className="rfpp-exhausted-desc">
              Você experimentou uma amostra de <strong>{data.subdeckName}</strong>.<br />
              Para ter acesso completo, adquira o material.
            </p>
            <div className="rfpp-exhausted-stats">
              {cardsRated} {cardsRated === 1 ? 'card estudado' : 'cards estudados'}
            </div>
            {data.productSlug && (
              <button
                className="rfpp-cta-btn"
                onClick={() => navigate(`/ryan/produto/${data.productSlug}`)}
              >
                <ShoppingBag size={16} />
                Ver produto completo
              </button>
            )}
            {returnPath && (
              <button className="rfpp-back-link" onClick={() => navigate(returnPath)}>
                Voltar ao campo
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="main-content-final">
          <div className="card-area-final">
            <div
              className={`flashcard-final ${isFlipped ? 'flipped' : ''}`}
              onClick={handleFlip}
            >
              <div onClick={e => e.stopPropagation()}>
                <PreviewCardActionsMenu
                  hasSupportContent={hasSupportContent}
                  onToggleSupportContent={() => setShowSupportContent(v => !v)}
                  onShowToast={(msg, type) => setToast({ message: msg, type })}
                  cardId={currentCard._id}
                />
              </div>

              <div className="flashcard-front">
                <div className="card-content-wrapper">
                  <div className="card-content">
                    <div
                      className={isTransitioning ? 'content-transitioning' : ''}
                      dangerouslySetInnerHTML={renderCardContent(currentCard.front)}
                    />
                  </div>
                </div>
              </div>

              <div className="flashcard-back">
                <div className="card-content-wrapper">
                  <div className="card-content">
                    <div
                      className={isTransitioning ? 'content-transitioning' : ''}
                      dangerouslySetInnerHTML={renderCardContent(currentCard.back)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {!isFlipped ? (
              <div className="rfpp-question-controls">
                <button
                  className="control-btn btn-show-answer-inline rfpp-show-btn"
                  onClick={handleFlip}
                >
                  Ver resposta
                </button>
                {currentIndex + 1 < cards.length && (
                  <button
                    className="control-btn btn-skip-inline rfpp-skip-btn"
                    onClick={() => handleRate(0)}
                    disabled={isTransitioning}
                  >
                    Pular →
                  </button>
                )}
              </div>
            ) : (
              <div className="answer-controls">
                <p className="evaluation-text">Como você avalia seu conhecimento?</p>
                <div className="rating-buttons-inline">
                  {[1, 2, 3, 4, 5].map(rating => (
                    <button
                      key={rating}
                      className={`rating-btn-inline level-${rating}`}
                      onClick={e => { e.stopPropagation(); handleRate(rating); }}
                      disabled={isTransitioning}
                    >
                      <span className="rating-num">{rating}</span>
                      <span className="rating-txt">
                        {rating === 1 && 'Muito\nDifícil'}
                        {rating === 2 && 'Difícil'}
                        {rating === 3 && 'Bom'}
                        {rating === 4 && 'Fácil'}
                        {rating === 5 && 'Muito\nFácil'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Statistics sidebar (identical structure to StudySidebarPanel) ── */}
      {showStats && (
        <>
          <div className="sidebar-overlay" onClick={() => setShowStats(false)} />
          <div className="sidebar-panel">

            <div className="sidebar-header-new">
              <div className="sidebar-tabs-inline">
                <button
                  className={`sidebar-tab-btn ${statsTab === 'session' ? 'sidebar-tab-btn--active' : ''}`}
                  onClick={() => setStatsTab('session')}
                >
                  Sessão atual
                </button>
                <button
                  className={`sidebar-tab-btn ${statsTab === 'progress' ? 'sidebar-tab-btn--active' : ''}`}
                  onClick={() => setStatsTab('progress')}
                >
                  Progresso
                </button>
              </div>
              <button className="btn-close-sidebar-new" onClick={() => setShowStats(false)} aria-label="Fechar">
                <X size={14} />
              </button>
            </div>

            <div className="rfpp-sidebar-locked">
              {/* Blurred fake content — purely visual */}
              <div className="rfpp-sidebar-blurred" aria-hidden="true">
                {statsTab === 'session' ? (
                  <div className="sidebar-content">
                    <div className="stats-basic">
                      <div className="stat-simple">
                        <span className="stat-simple-value">{cardsRated}</span>
                        <span className="stat-simple-label">Cards estudados</span>
                      </div>
                      <div className="stat-simple">
                        <span className="stat-simple-value">—</span>
                        <span className="stat-simple-label">Avaliações 4-5</span>
                      </div>
                    </div>
                    <div className="precision-highlight">
                      <div className="precision-circle">
                        <svg width="132" height="132" viewBox="0 0 132 132">
                          <circle cx="66" cy="66" r="60" fill="none"
                            stroke="rgba(47,113,48,0.12)" strokeWidth="4" />
                          <circle cx="66" cy="66" r="60" fill="none"
                            stroke="#2F7130" strokeWidth="4"
                            strokeDasharray="245 376.99"
                            strokeLinecap="round"
                            transform="rotate(-90 66 66)" />
                        </svg>
                        <div className="precision-number">65%</div>
                      </div>
                      <span className="precision-label">Precisão da sessão</span>
                    </div>
                    <div className="ratings-section-quiet">
                      <h4 className="section-title-quiet">Distribuição</h4>
                      <div className="rating-distribution-quiet">
                        {([
                          [1, 30, 3], [2, 20, 2], [3, 15, 2], [4, 25, 3], [5, 10, 1],
                        ] as [number, number, number][]).map(([level, pct, count]) => (
                          <div key={level} className="rating-row-quiet">
                            <span className={`rating-label-quiet level-${level}`}>{level}</span>
                            <div className="rating-bar-wrapper-quiet">
                              <div className={`rating-bar-quiet level-${level}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="rating-count-quiet">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="sidebar-content sidebar-content--progress">
                    <div className="prog-block">
                      <div className="prog-mastery-compact">
                        <span className="prog-mastery-value-sm">72%</span>
                        <div className="prog-mastery-right">
                          <span className="prog-mastery-label">domínio acumulado</span>
                          <span className="prog-delta-inline prog-delta--up">+8% desde a última sessão</span>
                        </div>
                      </div>
                      <div className="prog-bar-bg">
                        <div className="prog-bar-fill" style={{ width: '72%' }} />
                      </div>
                    </div>
                    <div className="prog-block">
                      <div className="prog-coverage-compact">
                        <span className="prog-coverage-nums-sm"><strong>48</strong> / 80</span>
                        <span className="prog-coverage-label">cards únicos estudados · 60% do deck</span>
                      </div>
                      <div className="prog-bar-bg prog-bar-bg--slim">
                        <div className="prog-bar-fill prog-bar-fill--neutral" style={{ width: '60%' }} />
                      </div>
                    </div>
                    <div className="prog-block">
                      <span className="prog-block-title">Distribuição acumulada</span>
                      <div className="prog-dist-grid">
                        {FAKE_DIST_ROWS.map(({ label, color, count, pct }) => (
                          <div key={label} className="prog-dist-row">
                            <span className="prog-dist-label">{label}</span>
                            <div className="prog-dist-bar-bg">
                              <div className="prog-dist-bar-fill" style={{ width: `${pct}%`, background: color }} />
                            </div>
                            <span className="prog-dist-count">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="prog-block">
                      <span className="prog-block-title">Última sessão · hoje, 14:32</span>
                      <div className="prog-last-compact">
                        <div className="prog-last-item">
                          <span className="prog-last-value-sm">21</span>
                          <span className="prog-last-label">cards</span>
                        </div>
                        <div className="prog-last-item">
                          <span className="prog-last-value-sm">78%</span>
                          <span className="prog-last-label">precisão</span>
                        </div>
                        <div className="prog-comparison-inline prog-comparison--up">+6% precisão</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lock overlay — prominently visible */}
              <div className="rfpp-sidebar-lock">
                <div className="rfpp-lock-icon-wrap">
                  <Lock size={28} strokeWidth={2} color="#0D3B0E" />
                </div>
                <p className="rfpp-lock-title">Estatísticas detalhadas</p>
                <p className="rfpp-lock-desc">
                  Assine para ter acesso às estatísticas completas de sessão e histórico de progresso.
                </p>
                {data.productSlug && (
                  <button
                    className="rfpp-cta-btn rfpp-cta-sm"
                    onClick={() => { navigate(`/ryan/produto/${data.productSlug!}`); setShowStats(false); }}
                  >
                    <ShoppingBag size={14} />
                    Ver produto
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <SupportContent
        isVisible={showSupportContent}
        content={currentCard.supportContent || ''}
        onClose={() => setShowSupportContent(false)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default RyanFlashcardPreviewPage;
